import axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  InternalAxiosRequestConfig,
} from "axios";
import {
  API_BASE_URL,
  API_TIMEOUT,
  HTTP_STATUS,
  TOAST_DURATION,
} from "constants/index";
import { tokenStorage } from "utils/storage";
import {
  isRegistrationPage,
  mayClearAuthSession,
  shouldOmitAuthForRequest,
  shouldSkipSessionClearOn401,
} from "utils/publicApi";
import { ApiError, ApiResponse } from "types/index";
import { useUIStore } from "store/uiStore";
import {
  clearAuthSession,
  isAccessTokenExpired,
} from "utils/authSession";

export interface ExtendedAxiosRequestConfig extends InternalAxiosRequestConfig {
  metadata?: {
    requestId: string;
    skipGlobalLoading?: boolean;
  };
}

export type RefreshSubscriber = (token: string) => void;

export const STANDARD_API_ERROR_MESSAGE =
  "An error occurred on the server. Please contact the administrator.";

export function createBaseAxiosInstance(): AxiosInstance {
  return axios.create({
    baseURL: API_BASE_URL,
    timeout: API_TIMEOUT,
    headers: {
      "Content-Type": "application/json",
    },
  });
}

export function isManualLogoutFlag(): boolean {
  return sessionStorage.getItem("__manual_logout__") === "true";
}

export function showTokenExpiredToast(): void {
  if (isManualLogoutFlag()) return;
  useUIStore.getState().showToast({
    type: "error",
    message: "Token expired. You will be logged out.",
    title: "Token Expired",
    duration: TOAST_DURATION.LONG,
  });
}

export function formatApiError(error: AxiosError): ApiError {
  if (error.response) {
    const { data, status } = error.response;
    return {
      message:
        (data as any)?.error ||
        (data as any)?.message ||
        STANDARD_API_ERROR_MESSAGE,
      code: (data as any)?.code,
      statusCode: status,
      errors: (data as any)?.errors,
    };
  }
  if (error.request) {
    return {
      message: STANDARD_API_ERROR_MESSAGE,
      code: "NETWORK_ERROR",
    };
  }
  return {
    message: error.message || STANDARD_API_ERROR_MESSAGE,
    code: "UNKNOWN_ERROR",
  };
}

export function shouldShowGlobalLoader(url: string, method: string): boolean {
  const isPageList =
    url.endsWith("/getpagelist") || url.includes("/getpagelist?");
  const isCreateOrUpdate =
    method === "post" || method === "put" || method === "patch";
  const isPublicProfile =
    url.includes("/employee/encrypted/") ||
    url.includes("/individual/encrypted/");
  return isPageList || isCreateOrUpdate || isPublicProfile;
}

/** Attach requestId / skipGlobalLoading metadata; returns whether to track loading. */
export function attachRequestLoadingMetadata(
  config: ExtendedAxiosRequestConfig
): { requestId: string; skipGlobalLoading: boolean } {
  const requestId = `${config.method?.toUpperCase()}_${config.url}_${Date.now()}`;
  const url = (config.url || "").toLowerCase();
  const method = (config.method || "get").toLowerCase();
  const shouldShowGlobal = shouldShowGlobalLoader(url, method);
  const explicitFlag = config.metadata?.skipGlobalLoading;
  const skipGlobalLoading =
    explicitFlag !== undefined ? explicitFlag : !shouldShowGlobal;
  config.metadata = { requestId, skipGlobalLoading };
  return { requestId, skipGlobalLoading };
}

export function updateGlobalLoadingState(activeRequests: Set<string>): void {
  const { setLoading } = useUIStore.getState();
  setLoading(activeRequests.size > 0, "");
}

export function releaseActiveRequest(
  activeRequests: Set<string>,
  config: ExtendedAxiosRequestConfig | undefined
): void {
  const requestId = config?.metadata?.requestId;
  const skipGlobalLoading = config?.metadata?.skipGlobalLoading;
  if (requestId && !skipGlobalLoading) {
    activeRequests.delete(requestId);
    updateGlobalLoadingState(activeRequests);
  }
}

/**
 * Mutates config with Authorization when appropriate.
 * Returns a rejected Promise when the request should not proceed (expired token).
 */
export function applyAuthToRequest(
  config: ExtendedAxiosRequestConfig
): ExtendedAxiosRequestConfig | Promise<never> {
  const requestUrl = (config.url || "").toLowerCase();
  const onRegister = isRegistrationPage();
  const omitAuth = shouldOmitAuthForRequest(requestUrl) || onRegister;
  const token = tokenStorage.getAccessToken();

  if (omitAuth) {
    if (config.headers?.Authorization) {
      delete config.headers.Authorization;
    }
  } else if (token) {
    if (
      isAccessTokenExpired(token) &&
      !tokenStorage.isRecentlyAuthenticated()
    ) {
      if (!mayClearAuthSession()) {
        if (config.headers?.Authorization) {
          delete config.headers.Authorization;
        }
      } else {
        showTokenExpiredToast();
        clearAuthSession();
        return Promise.reject(new Error("Token expired"));
      }
    } else {
      config.headers.Authorization = `Bearer ${token}`;
    }
  }

  return config;
}

type RefreshContext = {
  axiosInstance: AxiosInstance;
  isRefreshing: boolean;
  refreshSubscribers: RefreshSubscriber[];
  logoutInProgress: boolean;
  setRefreshing: (value: boolean) => void;
  setLogoutInProgress: (value: boolean) => void;
  clearRefreshSubscribers: () => void;
  post: <T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ) => Promise<ApiResponse<T>>;
};

/**
 * Shared 401 refresh-queue handling used by both authenticated and no-token clients.
 * Returns a Promise when this path handles the error, or null to fall through.
 */
export function handleUnauthorizedRefresh(
  error: AxiosError,
  ctx: RefreshContext
): Promise<unknown> | null {
  const originalRequest = error.config as AxiosRequestConfig & {
    _retry?: boolean;
  };

  if (
    error.response?.status !== HTTP_STATUS.UNAUTHORIZED ||
    !originalRequest ||
    originalRequest._retry
  ) {
    return null;
  }

  const requestUrl = (originalRequest.url || "").toLowerCase();
  if (shouldSkipSessionClearOn401(requestUrl) || !mayClearAuthSession()) {
    return Promise.reject(formatApiError(error));
  }
  if (
    requestUrl.includes("/auth/signin") ||
    requestUrl.includes("/auth/login") ||
    requestUrl.includes("/auth/refresh")
  ) {
    return Promise.reject(formatApiError(error));
  }

  if (ctx.isRefreshing) {
    return new Promise((resolve) => {
      ctx.refreshSubscribers.push((token: string) => {
        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${token}`;
        }
        resolve(ctx.axiosInstance(originalRequest));
      });
    });
  }

  originalRequest._retry = true;
  ctx.setRefreshing(true);

  return (async () => {
    try {
      const refreshToken = tokenStorage.getRefreshToken();
      if (!refreshToken) {
        if (!ctx.logoutInProgress) {
          ctx.setLogoutInProgress(true);
          showTokenExpiredToast();
          clearAuthSession();
          setTimeout(() => {
            ctx.setLogoutInProgress(false);
          }, 500);
        }
        // return (not throw) so the catch below does not double-toast/clear
        return Promise.reject(formatApiError(error));
      }

      const response = await ctx.post<{ accessToken: string }>("/auth/refresh", {
        refreshToken,
      });

      const { accessToken } = response.data;
      tokenStorage.setTokens(accessToken, refreshToken);

      ctx.refreshSubscribers.forEach((callback) => callback(accessToken));
      ctx.clearRefreshSubscribers();

      if (originalRequest.headers) {
        originalRequest.headers.Authorization = `Bearer ${accessToken}`;
      }

      return ctx.axiosInstance(originalRequest);
    } catch {
      showTokenExpiredToast();
      clearAuthSession();
      return Promise.reject(formatApiError(error));
    } finally {
      ctx.setRefreshing(false);
    }
  })();
}

export type CreateApiClientOptions = {
  withAuth: boolean;
};

/**
 * Shared HTTP client. Public get/post/put/patch/delete match the no-token service.
 * The auth service wraps post for its params overload and adds resendCode / downloadBlob.
 */
export class SharedApiClient {
  protected axiosInstance: AxiosInstance;
  private isRefreshing = false;
  private refreshSubscribers: RefreshSubscriber[] = [];
  private activeRequests = new Set<string>();
  private logoutInProgress = false;
  private readonly withAuth: boolean;

  constructor(options: CreateApiClientOptions) {
    this.withAuth = options.withAuth;
    this.axiosInstance = createBaseAxiosInstance();
    this.setupInterceptors();
  }

  private setupInterceptors(): void {
    this.axiosInstance.interceptors.request.use(
      (config: ExtendedAxiosRequestConfig) => {
        if (this.withAuth) {
          const authResult = applyAuthToRequest(config);
          if (authResult instanceof Promise) {
            return authResult;
          }
        }

        const { requestId, skipGlobalLoading } =
          attachRequestLoadingMetadata(config);
        if (!skipGlobalLoading) {
          this.activeRequests.add(requestId);
          updateGlobalLoadingState(this.activeRequests);
        }

        return config;
      },
      (error) => Promise.reject(error)
    );

    this.axiosInstance.interceptors.response.use(
      (response) => {
        releaseActiveRequest(
          this.activeRequests,
          response.config as ExtendedAxiosRequestConfig
        );
        return response;
      },
      (error: AxiosError) => {
        releaseActiveRequest(
          this.activeRequests,
          error.config as ExtendedAxiosRequestConfig | undefined
        );

        const handled = handleUnauthorizedRefresh(error, {
          axiosInstance: this.axiosInstance,
          isRefreshing: this.isRefreshing,
          refreshSubscribers: this.refreshSubscribers,
          logoutInProgress: this.logoutInProgress,
          setRefreshing: (value) => {
            this.isRefreshing = value;
          },
          setLogoutInProgress: (value) => {
            this.logoutInProgress = value;
          },
          clearRefreshSubscribers: () => {
            this.refreshSubscribers = [];
          },
          post: this.post.bind(this),
        });

        if (handled) {
          return handled;
        }

        return Promise.reject(formatApiError(error));
      }
    );
  }

  async get<T = any>(
    url: string,
    config?: AxiosRequestConfig
  ): Promise<ApiResponse<T>> {
    const response = await this.axiosInstance.get(url, config);
    return response.data;
  }

  async post<T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<ApiResponse<T>> {
    const response = await this.axiosInstance.post(url, data, config);
    return response.data;
  }

  async put<T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<ApiResponse<T>> {
    const response = await this.axiosInstance.put(url, data, config);
    return response.data;
  }

  async patch<T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<ApiResponse<T>> {
    const response = await this.axiosInstance.patch(url, data, config);
    return response.data;
  }

  async delete<T = any>(
    url: string,
    config?: AxiosRequestConfig
  ): Promise<ApiResponse<T>> {
    const response = await this.axiosInstance.delete(url, config);
    return response.data;
  }

  async uploadFile<T = any>(
    url: string,
    file: File,
    onUploadProgress?: (progressEvent: any) => void
  ): Promise<ApiResponse<T>> {
    const formData = new FormData();
    formData.append("file", file);

    const response = await this.axiosInstance.post(url, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
      onUploadProgress,
    });

    return response.data;
  }

  getInstance(): AxiosInstance {
    return this.axiosInstance;
  }
}

export function createApiClient(
  options: CreateApiClientOptions
): SharedApiClient {
  return new SharedApiClient(options);
}
